import { useCallback, useEffect, useState, type JSX } from 'react';
import { ApiError, api, type RevisionMeta } from '../api/client';
import { useInstance } from '../frame/InstanceContext';
import type { DiffSelection, DiffSide } from './DiffView';

/**
 * Version history (ADR-006 amendment). Lists the document's whole-document
 * snapshots — saved AUTOMATICALLY as it's edited (plus a "before restore"
 * checkpoint whenever you roll back) — and lets the Commission revert the *whole*
 * document to one, or COMPARE any two versions (pick an A base + a B compare, where
 * "Current" is the live document). Restore is ID-preserving on the server, so
 * commentary stays anchored; it records a "before restore" checkpoint first, so a
 * restore is itself undoable. Commission-only (the worker gates every route).
 * Rendered in place of the reading surface; closes on click-away / Escape.
 */

interface HistoryPanelProps {
  docId: string;
  /** Re-fetch the document after a restore so the surface reflects the rollback. */
  onReload: () => void | Promise<void>;
  /** Open the Compare view for the chosen A (base) → B (compare) pair. */
  onCompare: (selection: DiffSelection) => void;
}

const fmt = (iso: string): string =>
  new Date(iso).toLocaleString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export function HistoryPanel({ docId, onReload, onCompare }: HistoryPanelProps): JSX.Element {
  const compareEnabled = useInstance().features.compare;
  const [revisions, setRevisions] = useState<RevisionMeta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Compare selection: A is the base ("before"), B the compared ("after").
  const [sideA, setSideA] = useState<DiffSide | null>(null);
  const [sideB, setSideB] = useState<DiffSide>('current');

  const load = useCallback(async () => {
    try {
      const { revisions } = await api.revisions(docId);
      setRevisions(revisions);
      // Default A to the newest revision so "newest → current" is a one-click compare.
      setSideA((a) => a ?? revisions[0]?.id ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load version history.');
    }
  }, [docId]);

  useEffect(() => {
    void load();
  }, [load]);

  const restore = async (rev: RevisionMeta) => {
    const when = rev.label ? `“${rev.label}” (${fmt(rev.createdAt)})` : fmt(rev.createdAt);
    if (
      !window.confirm(
        `Restore the whole document to ${when}?\n\n` +
          'Everything since then is replaced. Your current version is saved as a ' +
          '"before restore" checkpoint first, so you can undo this.',
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await api.restoreRevision(docId, rev.id);
      await onReload();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not restore that version.');
    } finally {
      setBusy(false);
    }
  };

  const canCompare = sideA !== null && sideB !== null && sideA !== sideB;

  const pick = (side: DiffSide): JSX.Element => (
    <span className="history-pick">
      <label className="history-pick-opt">
        <input
          type="radio"
          name="diff-a"
          checked={sideA === side}
          onChange={() => setSideA(side)}
        />
        A
      </label>
      <label className="history-pick-opt">
        <input
          type="radio"
          name="diff-b"
          checked={sideB === side}
          onChange={() => setSideB(side)}
        />
        B
      </label>
    </span>
  );

  return (
    <section className="history-panel" aria-label="Version history">
      <div className="history-head">
        <h2>Version history</h2>
        <p className="history-hint">
          Saved automatically as you edit.
          {compareEnabled && ' Click a version’s summary to see what it changed.'}
        </p>
      </div>

      {compareEnabled && (
        <div className="history-comparebar">
          <span>Compare any two — tick an A and a B:</span>
          <button
            type="button"
            className="btn-compact"
            disabled={!canCompare}
            onClick={() => canCompare && onCompare({ a: sideA, b: sideB })}
          >
            Compare A → B
          </button>
        </div>
      )}

      {error && <p className="history-error">{error}</p>}

      {revisions === null ? (
        <p className="history-empty">Loading…</p>
      ) : (
        <ol className="history-list">
          <li className="history-row synthetic">
            {compareEnabled && pick('current')}
            <span className="history-when">
              <span className="history-label">Current version</span>
              <span className="history-time">live document</span>
            </span>
          </li>
          {revisions.length === 0 ? (
            <p className="history-empty">
              No saved versions yet — they’ll appear here automatically as you edit.
            </p>
          ) : (
            revisions.map((rev, i) => {
              const predecessor = revisions[i + 1]; // the older save (list is newest-first)
              return (
                <li key={rev.id} className={`history-row${rev.label ? ' labelled' : ''}`}>
                  {compareEnabled && pick(rev.id)}
                  <span className="history-when">
                    {rev.label && <span className="history-label">{rev.label}</span>}
                    <span className="history-time">{fmt(rev.createdAt)}</span>
                    {rev.author && <span className="history-author">{rev.author}</span>}
                    {rev.changeSummary &&
                      (compareEnabled && predecessor ? (
                        <button
                          type="button"
                          className="history-change"
                          title="See what changed in this version"
                          onClick={() => onCompare({ a: predecessor.id, b: rev.id })}
                        >
                          {rev.changeSummary} →
                        </button>
                      ) : (
                        <span className="history-change">{rev.changeSummary}</span>
                      ))}
                  </span>
                  <span className="history-actions">
                    <button
                      type="button"
                      className="link-btn"
                      disabled={busy}
                      onClick={() => restore(rev)}
                    >
                      Restore
                    </button>
                  </span>
                </li>
              );
            })
          )}
        </ol>
      )}
    </section>
  );
}
